import { useEffect, useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { ApiError } from '../api/client';
import { authApi } from '../api/endpoints';
import { useAuth } from '../app/AuthProvider';
import { useToast } from '../app/ToastProvider';

export function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { refreshUser } = useAuth();
  const toast = useToast();

  useEffect(() => {
    if (open) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError(null);
    }
  }, [open]);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { message } = await authApi.changePassword(currentPassword, newPassword, confirmPassword);
      await refreshUser();
      toast.success(message);
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Your password could not be updated.', 0));
    } finally {
      setSubmitting(false);
    }
  };

  const fields = error?.byField() ?? {};

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Change password</DialogTitle>
      <form onSubmit={submit}>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error ? <Alert severity="error">{error.message}</Alert> : null}
            <TextField label="Current password" type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} error={Boolean(fields.currentPassword)} helperText={fields.currentPassword} autoComplete="current-password" required autoFocus />
            <TextField label="New password" type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} error={Boolean(fields.newPassword)} helperText={fields.newPassword ?? 'Use at least 8 characters.'} slotProps={{ htmlInput: { minLength: 8, maxLength: 128 } }} autoComplete="new-password" required />
            <TextField label="Confirm new password" type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} error={Boolean(fields.confirmPassword)} helperText={fields.confirmPassword} autoComplete="new-password" required />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting}>{submitting ? 'Updating...' : 'Change Password'}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
